<template>
  <div
    ref="dropzone"
    class="dropzone"
    @[dragenterEvent]="preventDefaults"
    @[dragoverEvent]="dropzoneDragOver"
    @[dragleaveEvent]="dropzoneDragLeave"
    @[dropEvent]="dropzoneDragDrop"
  >
    <div class="dropzone-content">
      <slot />
    </div>
    <div v-show="mustHighlightCheck" class="dropzone-border">
      <div
        class="d-flex flex-column justify-end align-center pb-4"
        style="width: 100%; height: 100%"
      >
        <div
          class="dropzone-snackbar-icon"
          :class="$vuetify.theme.dark ? 'dark-snackbar-icon' : ''"
        >
          <v-icon class="mb-2" color="primary" size="35px">
            mdi-cloud-upload-outline
          </v-icon>
        </div>
        <div class="d-flex flex-column mt-3 dropzone-snackbar">
          <div v-if="currentFolder">
            <span>
              {{ $t('message.pages.droparea.folderMessage') }}
            </span>
            <span>
              <v-icon
                color="white"
                size="16"
                style="margin-bottom: 2px"
                class="mr-1"
              >
                mdi-folder </v-icon
              >{{ stripFolderName(currentFolder) }}
            </span>
          </div>
          <span v-else>{{ $t('message.pages.droparea.noFolderMessage') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'

import foldersApi from '@/services/foldersApi'

export default {
  name: 'DropArea',
  props: {
    handleAddFile: Function,
    currentFolder: {
      type: String,
      required: false,
    },
    canHighlight: {
      type: Boolean,
      default: false,
    },
    unbindEvent: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data: () => ({
    files: [],
    tree: [],
    inTreeFiles: [],
    mustHighlight: false,
    relatedTarget: null,
  }),
  computed: {
    mouseoutEvent() {
      return this.unbindEvent ? null : 'mouseout'
    },
    dragenterEvent() {
      return this.unbindEvent ? null : 'dragenter'
    },
    dragoverEvent() {
      return this.unbindEvent ? null : 'dragover'
    },
    dragleaveEvent() {
      return this.unbindEvent ? null : 'dragleave'
    },
    dropEvent() {
      return this.unbindEvent ? null : 'drop'
    },
    mustHighlightCheck() {
      if (this.mustHighlight) {
        return true
      }

      return false
    },
  },
  mounted() {
    window.addEventListener('mouseout', this.windowMouseOut)
    document.addEventListener('dragenter', this.documentDragEnter)
    document.addEventListener('dragleave', this.dragLeaveHandler)
  },
  beforeDestroy() {
    window.removeEventListener('mouseout', this.windowMouseOut)
    document.removeEventListener('dragenter', this.documentDragEnter)
    document.removeEventListener('dragleave', this.dragLeaveHandler)
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('user', ['addUser', 'editUser', 'addFolder', 'editFolder']),
    preventDefaults(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    windowMouseOut() {
      this.mustHighlight = false
    },
    documentDragEnter(e) {
      this.relatedTarget = e.target
    },
    dropzoneDragOver(e) {
      this.preventDefaults(e)

      if (this.canHighlight) {
        this.relatedTarget = e.target
        this.mustHighlight = true
      }
    },
    dropzoneDragLeave(e) {
      this.preventDefaults(e)
      this.$emit('unbindEvent', false)
    },
    dragLeaveHandler(e) {
      this.preventDefaults(e)
      this.mustHighlight = false
    },
    async dropzoneDragDrop(e) {
      this.preventDefaults(e)

      this.mustHighlight = false

      const files = e.dataTransfer.files
      const items = e.dataTransfer.items
      const toBeReadItems = []

      files.forEach((file) => {
        if (file.type && file.type.includes('video')) {
          this.handleAddFile({ file: file })
        }
      })

      items.forEach((item) => {
        toBeReadItems.push(item.webkitGetAsEntry())
      })

      if (!toBeReadItems.length || !files.length || !items.length) return
      this.$root.$emit('setGlobalLoading', {
        open: true,
        text: 'Criando pastas...',
      })

      for (let entryItem of toBeReadItems) {
        if (entryItem.isDirectory) {
          if (entryItem) {
            this.tree.push({
              name: entryItem.name,
              fullPath: entryItem.fullPath,
              children: [],
            })
            await this.traverseFileTree(entryItem)
          }
        }

        if (this.$route.params.id) {
          await this.loopThroughTree(this.tree, {
            id: this.$route.params.id,
          })
        } else {
          await this.loopThroughTree(this.tree)
        }

        await this.loopThroughVideos()

        this.tree = []
        this.inTreeFiles = []
      }

      this.$root.$emit('setGlobalLoading', { open: false, text: '' })
    },
    stripFolderName(folderName) {
      if (folderName.length > 30) {
        return `${folderName.slice(0, 30)}...`
      }
      return `${folderName}.`
    },
    async traverseFileTree(item) {
      let pathArray = item.fullPath.split('/')
      pathArray.shift()
      if (item.isFile) {
        item.file((file) => {
          this.inTreeFiles.push({
            file: file,
            folderPath: item.fullPath.replace(`/${item.name}`, ''),
          })
        })
      } else if (item.isDirectory) {
        if (pathArray.length > 1) {
          let findParentFolder = this.getSubMenuItem(
            this.tree,
            pathArray[pathArray.length - 2]
          )
          if (findParentFolder) {
            findParentFolder.children.push({
              name: item.name,
              fullPath: item.fullPath,
              children: [],
            })
          }
        }
        // Get folder contents
        let dirReader = item.createReader()
        await new Promise((resolve) => {
          dirReader.readEntries(async (entries) => {
            for (var i = 0; i < entries.length; i++) {
              await this.traverseFileTree(entries[i])
            }
            resolve()
          })
        })
      }
    },
    getSubMenuItem(subMenuItems, name) {
      if (subMenuItems) {
        for (let i = 0; i < subMenuItems.length; i++) {
          if (subMenuItems[i].name == name) {
            return subMenuItems[i]
          }
          let found = this.getSubMenuItem(subMenuItems[i].children, name)
          if (found) return found
        }
      }
    },
    async loopThroughTree(node, parent) {
      for (let obj of node) {
        if (parent) {
          let id = await this.createFolder(obj.name, parent.id)
          obj.id = id
        } else {
          let id = await this.createFolder(obj.name)
          obj.id = id
        }
        this.inTreeFiles = this.inTreeFiles.map((vid) =>
          vid.folderPath === obj.fullPath ? { ...vid, folder_id: obj.id } : vid
        )
        if (obj.children) {
          await this.loopThroughTree(obj.children, obj)
        }
      }
    },
    async loopThroughVideos() {
      for (let video of this.inTreeFiles) {
        this.handleAddFile({ file: video.file, folderId: video.folder_id })
      }
    },
    async createFolder(name, id) {
      try {
        let folderObject = {
          name: name.trim(),
        }
        if (id) {
          folderObject['parent_folder_id'] = id
        }
        let ret = await foldersApi.createFolder(folderObject)
        this.addFolder(ret.data)
        this.$root.$emit('addFolderVideosPage', ret.data)
        return ret.data.id
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dropzone {
  position: relative;
  width: 100%;
  height: 100%;

  &-content {
    position: absolute;
    top: 0px;
    width: 100%;
  }

  &-border {
    position: relative;

    width: 100%;
    height: calc(100% - 15vh);
    background: #3e50f729;
    border: 2px solid var(--v-brand-base);
  }

  &-snackbar {
    border-radius: 4px;
    background: var(--v-brand-base);
    color: white;
    padding: 10px;

    &-icon {
      background: white;
      padding: 5px 10px 0px;
      border-radius: 4px;
    }
  }
}

.dark-snackbar-icon {
  background: var(--v-neutral-darken2);
}
</style>
